import { isDevelopment, isInExtension } from '~/helpers/utils';
import { AuthorizedResponse, PaywallUser } from '~/helpers/types';
import { useScriptTag } from '@vueuse/core';

export async function getPaywallUser(
    loadPaywallScript: boolean = false
): Promise<PaywallUser | undefined> {
    if (loadPaywallScript) {
        const loadProcess = new Promise((resolve, reject) => {
            // If paywall is not loaded, load it dynamically
            if (typeof window.paywall === 'undefined') {
                useScriptTag(
                    isDevelopment() ? '/js/payments-dev.js' : '/js/payments.js',
                    // on script tag loaded.
                    (el: HTMLScriptElement) => {
                        console.log('Paywall script loaded', el);
                        resolve(true);
                    }
                );
            } else {
                resolve(true);
            }
        });

        await loadProcess;
    }

    let userData: PaywallUser | undefined = undefined;

    try {
        userData = await Promise.race([
            window.paywall.getUser(),
            new Promise<PaywallUser>((_, reject) =>
                setTimeout(
                    () => reject(new Error('Timeout while fetching user data')),
                    4000
                )
            ),
        ]);
    } catch (error) {
        console.error('Error fetching user data:', error);
    }

    if (isDevelopment()) {
        console.log('[DEV] getPaywallUser()', userData);
    }

    return userData;
}

/**
 * Check authorized/premium/paid status
 * @returns {Promise<AuthorizedResponse>}
 */
export async function authorize(
    useCache: boolean = true
): Promise<AuthorizedResponse> {
    /*
        1. If cache, try to get from cache
        2. If cache is not available or expired
            a) if paywall script is not loaded, load it
            b) get info from paywall script
    */

    const ts = await getLastPaidStatusDate();
    const cacheExpired = Math.round(Date.now() / 1000) - ts >= 86400;
    if (isDevelopment()) {
        console.log(
            `[DEV] options.ts/authorise(): cache ${
                cacheExpired ? 'expired' : 'not expired'
            } (ts: ${ts})`
        );
    }

    if (useCache && !cacheExpired) {
        if (isInExtension()) {
            // from synced storage
            const rs = await chrome.storage.sync.get({
                htj: '',
                htjPaidUserEmail: '',
            });
            return {
                authorized: rs.htjPaidUserEmail ? true : false,
                paid: unmaskPaidStorageStatus(rs.htj),
                userEmail: rs.htjPaidUserEmail,
            };
        } else {
            // from local storage
            const paid = unmaskPaidStorageStatus(
                localStorage.getItem('htj') as string
            );
            const email = localStorage.getItem('htj_paid_user_email');
            return {
                authorized: email ? true : false,
                paid: paid,
                userEmail: email ?? '',
            };
        }
    }

    const userData = await getPaywallUser();

    const res: any = {
        authorized: userData?.user?.email ? true : false,
        paid: userData?.subscriptions && userData?.subscriptions?.length > 0,
        userEmail: userData?.user?.email,
    };

    savePaidStatus(res.paid);
    setPaidUserEmail(res.userEmail || '');
    setLastPaidStatusDate();

    return res;
}

/**
 * Save paid status
 * @param {boolean} paid
 */
export async function savePaidStatus(paid: boolean) {
    if (isInExtension()) {
        await chrome.storage.sync.set({
            etfi: maskPaidStorageStatus(paid),
        });
    } else {
        // to local storage
        localStorage.setItem('htj', maskPaidStorageStatus(paid));
    }
}

/**
 * Save paid user email
 * @param email
 */
export async function setPaidUserEmail(email: string) {
    if (isInExtension()) {
        await chrome.storage.sync.set({ etfiPaidUserEmail: email });
    } else {
        // to local storage
        localStorage.setItem('htj_paid_user_email', email);
    }
}

/**
 *
 * @returns {Promise<number>} timestamp, seconds
 */
export async function getLastPaidStatusDate(): Promise<number> {
    if (isInExtension()) {
        const rs = await chrome.storage.sync.get({
            etfiPaidStatusDate: 0,
        });
        return rs.etfiPaidStatusDate;
    }

    // from local storage
    let data = localStorage.getItem('htj_paid_status_date');
    let rs = data ? parseInt(data, 10) : 0;
    return isNaN(rs) ? 0 : rs;
}

/**
 *
 * @param {number} dateSeconds If 0, set to current time
 */
export async function setLastPaidStatusDate(dateSeconds: number = 0) {
    if (dateSeconds === 0) {
        dateSeconds = Math.round(Date.now() / 1000);
    }
    if (isInExtension()) {
        await chrome.storage.sync.set({ etfiPaidStatusDate: dateSeconds });
    } else {
        // to local storage
        localStorage.setItem('htj_paid_status_date', dateSeconds.toString());
    }
}

/**
 * Slightly masks false/true just to not keep it obvious in storage.
 * (Assuming that there are no much hackers around here)
 * @param paid
 */
export function maskPaidStorageStatus(paid: boolean): string {
    let key = '';

    for (let i = 0; i < 5; i++) {
        key += (Math.random() + 1).toString(36).substring(7);
    }
    // place 1|0 to the 18th position
    key = key.slice(0, 17) + (paid ? '1' : '0') + key.slice(18);

    return key;
}

/**
 * Unmask paid status
 * @param key
 */
export function unmaskPaidStorageStatus(key: string): boolean {
    if (key && key.length >= 18 && key.charAt(17) === '1') return true;
    return false;
}

export async function validatePromocode(
    promocode: string,
    userEmail?: string
): Promise<boolean> {
    try {
        const baseUrl = 'https://promocodes.heic-to-jpg.click/api/code';
        // const baseUrl = 'http://localhost:3000/api/code';

        const response = await fetch(
            `${baseUrl}/${promocode}?email=${userEmail}`,
            {
                // mode: 'no-cors',
            }
        );
        const data = await response.json();

        if (data.rs === 1) {
            return true;
        }
    } catch (error) {
        console.error('Error validating promocode:', error);
    }
    return false;
}

/**
 * Save promocode
 * @param promocode
 */
export async function setPromocode(promocode: string) {
    if (isInExtension()) {
        await chrome.storage.sync.set({ htj_promocode: promocode });
    } else {
        // to local storage
        localStorage.setItem('htj_promocode', promocode);
    }
}

/**
 * @returns {Promise<string>}
 */
export async function getPromocode(): Promise<string> {
    if (isInExtension()) {
        const rs = await chrome.storage.sync.get({
            htj_promocode: '',
        });
        return rs.htj_promocode;
    }

    // from local storage
    let data = localStorage.getItem('htj_promocode');
    return data ?? '';
}
